import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClockRotateLeft,
  faCode,
  faCodeCompare,
  faEye,
  faPaperPlane,
  faPenToSquare,
  faSpinner,
  faTriangleExclamation,
  faUpload,
  faXmark,
  faArrowUpRightDots,
} from '@fortawesome/free-solid-svg-icons';
import { lazyWithReload } from '@/lib/lazy-with-reload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/shared/FormField';
import { TemplateEditor, insertVariable } from '@/components/communication/TemplateEditor';
import { VariableBrowser } from '@/components/communication/VariableBrowser';
import type { HtmlSourceEditorApi } from '@/components/communication/HtmlSourceEditor';
import { IssueList } from '@/components/communication/HtmlIssueList';
import { CategoryBadge } from '@/components/communication/TemplateBadges';
import {
  getVisualEditorRisks,
  htmlToPlainText,
  validateHtml,
  checkEmailSafety,
} from '@/lib/email-html';
import { toast, swalConfirm } from '@/lib/swal';
import { cn } from '@/lib/cn';
import { useHasPermission } from '@/hooks/use-permissions';
import { emailGatewayService, type EmailGateway } from '@/services/email-gateway.service';
import { communicationTemplateService } from '@/services/communication-template.service';
import {
  emailTemplateLibraryService,
  type TemplateAdoption,
} from '@/services/email-template-library.service';
import { ADOPTION_PRESENTATION, AdoptionBadge, DraftBadge } from './adoption-presentation';
import { CompareView } from './CompareView';
import { HistoryTimeline } from './HistoryTimeline';
import { UpdateWindow } from './UpdateWindow';
import { SendTestDialog } from './SendTestDialog';

const HtmlSourceEditor = lazyWithReload(
  () =>
    import('@/components/communication/HtmlSourceEditor').then((m) => ({
      default: m.HtmlSourceEditor,
    })),
  'TenantHtmlSourceEditor',
);

type Tab = 'content' | 'source' | 'preview' | 'compare' | 'history' | 'update';

const VIEWPORTS = {
  desktop: { label: 'Desktop', width: 900 },
  tablet: { label: 'Tablet', width: 768 },
  mobile: { label: 'Mobile', width: 390 },
} as const;
type ViewportKey = keyof typeof VIEWPORTS;

interface Props {
  template: TemplateAdoption | null;
  onClose: () => void;
  onChanged: (next: TemplateAdoption) => void;
  onReload: () => void;
}

/**
 * Tenant template detail — Phase 12.5C.
 *
 * The organising principle is the OD-1 draft/live separation: at no point may
 * this component let a save look like a publish. Whenever a draft exists, the
 * live content is shown alongside it, Preview offers a Draft/Live toggle, and
 * Publish is the only control that carries a confirmation naming the consequence.
 */
export function TemplateAdoptionDrawer({ template, onClose, onChanged, onReload }: Props) {
  const can = useHasPermission();
  const canEdit = can('email-template:edit');
  const canEnable = can('email-template:enable');
  const canDisable = can('email-template:disable');
  const canRestore = can('email-template:restore');
  const canSendTest = can('email-template:preview');

  const [tab, setTab] = useState<Tab>('content');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [plainTextBody, setPlainTextBody] = useState('');
  const [gatewayId, setGatewayId] = useState<string>('');
  const [editor, setEditor] = useState<Editor | null>(null);
  const [sourceApi, setSourceApi] = useState<HtmlSourceEditorApi | null>(null);
  const [visualOptIn, setVisualOptIn] = useState(false);
  const [autoPlainText, setAutoPlainText] = useState(true);

  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [viewport, setViewport] = useState<ViewportKey>('desktop');
  const [previewMode, setPreviewMode] = useState<'draft' | 'live'>('draft');
  const [testOpen, setTestOpen] = useState(false);

  const [variables, setVariables] = useState<
    { key: string; label: string; module: string; exampleValue: string; description?: string }[]
  >([]);
  const [variablesLoading, setVariablesLoading] = useState(true);
  const [gateways, setGateways] = useState<EmailGateway[]>([]);

  const open = template !== null;

  // Variables and gateways are workspace-wide; fetched once per drawer session.
  useEffect(() => {
    if (!open) return;
    void (async () => {
      try {
        // The variable registry has no route on the library controller; the
        // tenant's only source is the legacy `/email-templates/variables`, which
        // is read-only, permission-gated on `view`, and returns process-wide
        // static metadata rather than tenant data.
        setVariables(await communicationTemplateService.getVariables());
      } catch {
        setVariables([]);
      } finally {
        setVariablesLoading(false);
      }
    })();
    void (async () => {
      try {
        setGateways(await emailGatewayService.list());
      } catch {
        setGateways([]);
      }
    })();
  }, [open]);

  // Load the working copy: the draft when one exists, otherwise the live content.
  useEffect(() => {
    if (!template) return;
    const startSubject = template.draftSubject ?? template.subject;
    const startHtml = template.draftHtmlBody ?? template.htmlBody;
    const startPlain = template.draftPlainTextBody ?? template.plainTextBody;
    setSubject(startSubject);
    setHtmlBody(startHtml);
    setPlainTextBody(startPlain);
    setGatewayId(template.assignedGatewayId ?? '');
    setTab(
      template.updateAvailable
        ? 'update'
        : getVisualEditorRisks(startHtml).length > 0
          ? 'source'
          : 'content',
    );
    setVisualOptIn(false);
    setActionError(null);
    setPreviewMode(template.hasUnpublishedChanges ? 'draft' : 'live');
    setAutoPlainText(!startPlain.trim() || startPlain.trim() === htmlToPlainText(startHtml).trim());
  }, [template]);

  const visualRisks = useMemo(() => getVisualEditorRisks(htmlBody), [htmlBody]);
  const structureIssues = useMemo(() => validateHtml(htmlBody), [htmlBody]);
  const safetyIssues = useMemo(() => checkEmailSafety(htmlBody), [htmlBody]);
  const blockingErrors = useMemo(
    () => [...structureIssues, ...safetyIssues].filter((i) => i.severity === 'error'),
    [structureIssues, safetyIssues],
  );

  /** Local edits not yet sent to the server. */
  const dirty =
    !!template &&
    (subject !== (template.draftSubject ?? template.subject) ||
      htmlBody !== (template.draftHtmlBody ?? template.htmlBody) ||
      plainTextBody !== (template.draftPlainTextBody ?? template.plainTextBody) ||
      gatewayId !== (template.assignedGatewayId ?? ''));

  // Unsaved-changes protection against a full page unload. In-app navigation is
  // covered by the close handler below.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const previewHtml = previewMode === 'draft' ? htmlBody : (template?.htmlBody ?? '');
  const previewSubject = previewMode === 'draft' ? subject : (template?.subject ?? '');

  const substituted = useMemo(() => {
    const samples = new Map(variables.map((v) => [v.key, v.exampleValue ?? '']));
    // Lower-case only, matching TemplateRendererService.substitute exactly, so the
    // preview cannot resolve a token the renderer would ship literally (D-032).
    const sub = (text: string) =>
      text.replace(/\{\{([a-z_][a-z0-9_]*)\}\}/g, (m, k: string) => samples.get(k) ?? m);
    return { subject: sub(previewSubject), htmlBody: sub(previewHtml) };
  }, [previewSubject, previewHtml, variables]);

  const handleClose = useCallback(async () => {
    if (dirty) {
      const ok = await swalConfirm({
        title: 'Discard unsaved edits?',
        text: 'Your changes have not been saved as a draft yet. Closing will lose them.',
        confirmText: 'Discard and close',
        cancelText: 'Keep editing',
      });
      if (!ok) return;
    }
    onClose();
  }, [dirty, onClose]);

  if (!open || !template) return null;

  const presentation = ADOPTION_PRESENTATION[template.adoptionStatus];

  /** Runs a lifecycle mutation, surfacing failure INLINE rather than as a bare toast. */
  const run = async (fn: () => Promise<TemplateAdoption>, successMsg: string) => {
    setBusy(true);
    setActionError(null);
    try {
      const next = await fn();
      onChanged(next);
      toast.success(successMsg);
      return next;
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'The action could not be completed.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleSaveDraft = async () => {
    const structural = blockingErrors;
    if (structural.length > 0) {
      setActionError(
        `Fix the HTML before saving: ${structural
          .slice(0, 3)
          .map((i) => i.message)
          .join(' · ')}`,
      );
      setTab('source');
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      const resolvedPlain = autoPlainText ? htmlToPlainText(htmlBody) : plainTextBody;
      const next = await emailTemplateLibraryService.saveDraft(template.templateKey, {
        subject,
        htmlBody,
        plainTextBody: resolvedPlain,
        assignedGatewayId: gatewayId === '' ? null : gatewayId,
      });
      if (autoPlainText) setPlainTextBody(resolvedPlain);
      onChanged(next);
      toast.success('Draft saved — your live emails are unchanged until you publish');
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'The draft could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (dirty) {
      setActionError('Save your draft before publishing.');
      return;
    }
    const ok = await swalConfirm({
      title: 'Publish this template?',
      html:
        '<p style="margin:0 0 8px">This replaces the version your workspace sends. ' +
        '<strong>Emails sent from now on will use the published content.</strong></p>' +
        '<p style="margin:0">Your workspace stops receiving automatic platform updates for this ' +
        'template — future updates will be offered for you to review.</p>',
      confirmText: 'Publish',
      cancelText: 'Cancel',
    });
    if (!ok) return;
    await run(
      () => emailTemplateLibraryService.publishDraft(template.templateKey),
      'Published — your workspace now sends this version',
    );
  };

  const handleDiscard = async () => {
    const ok = await swalConfirm({
      title: 'Discard draft?',
      text: 'Your unpublished changes will be removed. The version currently being sent is not affected.',
      confirmText: 'Discard draft',
    });
    if (!ok) return;
    const next = await run(
      () => emailTemplateLibraryService.discardDraft(template.templateKey),
      'Draft discarded',
    );
    if (next) {
      setSubject(next.subject);
      setHtmlBody(next.htmlBody);
      setPlainTextBody(next.plainTextBody);
    }
  };

  const handleDisable = async () => {
    const ok = await swalConfirm({
      title: 'Disable this template?',
      html:
        '<p style="margin:0"><strong>Emails using this template will be suppressed for your ' +
        'workspace.</strong> They will not be sent at all — the platform default does not take ' +
        'over.</p>',
      confirmText: 'Disable',
    });
    if (!ok) return;
    await run(
      () => emailTemplateLibraryService.disable(template.templateKey),
      'Template disabled — these emails are now suppressed',
    );
  };

  const handleReset = async () => {
    const ok = await swalConfirm({
      title: 'Reset to the platform version?',
      text: 'Your customization is archived in the history and your workspace goes back to the version maintained by your service provider.',
      confirmText: 'Reset',
    });
    if (!ok) return;
    const next = await run(
      () => emailTemplateLibraryService.resetToPlatform(template.templateKey),
      'Reset to the platform version',
    );
    if (next) {
      setSubject(next.subject);
      setHtmlBody(next.htmlBody);
      setPlainTextBody(next.plainTextBody);
    }
  };

  const TABS: { id: Tab; label: string; icon: typeof faEye; show: boolean }[] = [
    { id: 'content', label: 'Content', icon: faPenToSquare, show: true },
    { id: 'source', label: 'HTML Source', icon: faCode, show: true },
    { id: 'preview', label: 'Preview', icon: faEye, show: true },
    { id: 'compare', label: 'Compare', icon: faCodeCompare, show: true },
    { id: 'history', label: 'History', icon: faClockRotateLeft, show: true },
    {
      id: 'update',
      label: 'Update',
      icon: faArrowUpRightDots,
      show: template.updateAvailable || template.adoptionStatus === 'IGNORED',
    },
  ];

  const editingDisabled = !canEdit || saving || template.isArchived;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px]"
        onClick={() => void handleClose()}
        aria-hidden
      />

      <aside
        role="dialog"
        aria-label={`${template.name} details`}
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-card shadow-2xl sm:max-w-3xl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-3.5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-semibold text-foreground">{template.name}</h2>
              <AdoptionBadge status={template.adoptionStatus} />
              {template.hasUnpublishedChanges && <DraftBadge />}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[11px] text-muted-foreground">
                {template.templateKey}
              </span>
              <CategoryBadge category={template.category} />
            </div>
          </div>
          <button
            type="button"
            onClick={() => void handleClose()}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        </header>

        {/* What this state means for the recipient — stated, not implied. */}
        <p className="border-b border-border bg-muted/30 px-5 py-2 text-xs text-muted-foreground">
          {presentation.meaning}
        </p>

        <nav className="flex gap-1 overflow-x-auto border-b border-border px-3 py-1.5">
          {TABS.filter((t) => t.show).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'flex min-h-[36px] shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                tab === t.id
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <FontAwesomeIcon icon={t.icon} className="h-3 w-3" />
              {t.label}
            </button>
          ))}
        </nav>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {actionError && (
            <div
              role="alert"
              className="mb-3 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
              />
              <span>{actionError}</span>
            </div>
          )}

          {template.hasUnpublishedChanges && (
            <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              You have an unpublished draft. Your workspace is still sending the
              {template.isOverride ? ' previously published' : ' platform'} version until you
              publish.
            </div>
          )}

          {(tab === 'content' || tab === 'source') && (
            <div className="flex flex-col gap-4 lg:flex-row">
              <div className="min-w-0 flex-1 space-y-3">
                <FormField label="Subject" htmlFor="tt-subject">
                  <Input
                    id="tt-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={editingDisabled}
                    placeholder="Email subject line"
                  />
                </FormField>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    HTML Body
                  </label>
                  {tab === 'content' ? (
                    visualRisks.length > 0 && !visualOptIn ? (
                      <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
                        <p className="font-medium text-foreground">
                          This template needs the HTML Source editor
                        </p>
                        <p className="mt-1 text-muted-foreground">
                          It uses {visualRisks.join(', ')}, which the visual editor cannot represent
                          — editing here would permanently flatten the layout.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button type="button" size="sm" onClick={() => setTab('source')}>
                            <FontAwesomeIcon icon={faCode} className="mr-1.5 h-3 w-3" />
                            Edit HTML Source
                          </Button>
                          {canEdit && (
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={() => setVisualOptIn(true)}
                            >
                              Edit visually anyway
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <TemplateEditor
                        value={htmlBody}
                        onChange={setHtmlBody}
                        disabled={editingDisabled}
                        onEditorReady={setEditor}
                      />
                    )
                  ) : (
                    <Suspense
                      fallback={
                        <div className="flex h-[420px] items-center justify-center rounded-md border border-input">
                          <FontAwesomeIcon
                            icon={faSpinner}
                            className="h-5 w-5 animate-spin text-muted-foreground"
                          />
                        </div>
                      }
                    >
                      <HtmlSourceEditor
                        value={htmlBody}
                        onChange={setHtmlBody}
                        disabled={editingDisabled}
                        onReady={setSourceApi}
                      />
                    </Suspense>
                  )}
                </div>

                {tab === 'source' && (
                  <>
                    <IssueList
                      title="Validation"
                      issues={structureIssues}
                      emptyMessage="No structural problems found."
                    />
                    <IssueList
                      title="Email compatibility"
                      issues={safetyIssues}
                      emptyMessage="No email-client problems found."
                    />
                  </>
                )}

                <FormField label="Plain Text Body" htmlFor="tt-plain">
                  <label className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={autoPlainText}
                      onChange={(e) => setAutoPlainText(e.target.checked)}
                      disabled={editingDisabled}
                      className="h-3.5 w-3.5 accent-primary"
                    />
                    Auto-generate plain text from HTML on save
                  </label>
                  <textarea
                    id="tt-plain"
                    value={autoPlainText ? htmlToPlainText(htmlBody) : plainTextBody}
                    onChange={(e) => setPlainTextBody(e.target.value)}
                    disabled={editingDisabled || autoPlainText}
                    rows={5}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                  />
                </FormField>

                {/* OD-2 — routing, not content: applies as soon as the draft is saved. */}
                <FormField label="Email gateway" htmlFor="tt-gateway">
                  <select
                    id="tt-gateway"
                    value={gatewayId}
                    onChange={(e) => setGatewayId(e.target.value)}
                    disabled={editingDisabled}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                  >
                    <option value="">Use my workspace default</option>
                    {gateways.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Gateway choice is delivery configuration, not content — it takes effect as soon
                    as you save, without publishing.
                  </p>
                </FormField>
              </div>

              <div className="lg:w-72 lg:shrink-0">
                <p className="mb-1.5 text-sm font-medium text-foreground">Variables</p>
                <VariableBrowser
                  variables={variables}
                  loading={variablesLoading}
                  onInsert={(key) =>
                    tab === 'source'
                      ? sourceApi?.insertAtCursor(`{{${key}}}`)
                      : insertVariable(editor, key)
                  }
                />
              </div>
            </div>
          )}

          {tab === 'preview' && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-1.5">
                {(Object.keys(VIEWPORTS) as ViewportKey[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setViewport(key)}
                    className={cn(
                      'min-h-[36px] rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                      viewport === key
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {VIEWPORTS[key].label}
                  </button>
                ))}
                {template.hasUnpublishedChanges && (
                  <div className="ml-auto flex items-center gap-1.5 rounded-lg border border-border p-0.5">
                    {(['draft', 'live'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setPreviewMode(m)}
                        className={cn(
                          'min-h-[32px] rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                          previewMode === m
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {m === 'live' ? 'Currently sending' : 'Your draft'}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Subject</p>
                <p className="text-sm text-foreground">{substituted.subject}</p>
              </div>
              <div className="flex justify-center overflow-x-auto rounded-lg border border-border bg-muted/20 p-3">
                <iframe
                  title="Email preview"
                  srcDoc={substituted.htmlBody}
                  sandbox=""
                  style={{ width: VIEWPORTS[viewport].width }}
                  className="h-[520px] max-w-full rounded border border-border bg-white"
                />
              </div>
            </div>
          )}

          {tab === 'compare' && <CompareView templateKey={template.templateKey} />}
          {tab === 'history' && <HistoryTimeline templateKey={template.templateKey} />}
          {tab === 'update' && (
            <UpdateWindow
              template={template}
              canEdit={canEdit}
              onChanged={(next) => {
                onChanged(next);
                onReload();
              }}
            />
          )}
        </div>

        <footer className="flex flex-wrap items-center gap-1.5 border-t border-border px-5 py-3">
          {canEdit && !template.isArchived && (
            <>
              <Button
                size="sm"
                variant="secondary"
                className="min-h-[44px] sm:min-h-0"
                onClick={() => void handleSaveDraft()}
                disabled={!dirty || saving}
                title="Saves a draft. Your live emails are not affected."
              >
                {saving && (
                  <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
                )}
                Save Draft
              </Button>
              <Button
                size="sm"
                className="min-h-[44px] sm:min-h-0"
                onClick={() => void handlePublish()}
                disabled={busy || dirty || !template.hasUnpublishedChanges}
                title={
                  dirty
                    ? 'Save your draft before publishing'
                    : !template.hasUnpublishedChanges
                      ? 'There is nothing to publish'
                      : 'Publish the draft — this changes what your workspace sends'
                }
              >
                <FontAwesomeIcon icon={faUpload} className="mr-1.5 h-3 w-3" />
                Publish
              </Button>
              {template.hasUnpublishedChanges && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="min-h-[44px] sm:min-h-0"
                  onClick={() => void handleDiscard()}
                  disabled={busy}
                >
                  Discard Draft
                </Button>
              )}
            </>
          )}

          {canSendTest && (
            <Button
              size="sm"
              variant="secondary"
              className="min-h-[44px] sm:min-h-0"
              onClick={() => setTestOpen(true)}
              disabled={busy}
            >
              <FontAwesomeIcon icon={faPaperPlane} className="mr-1.5 h-3 w-3" />
              Send Test
            </Button>
          )}

          <span className="mx-1 hidden h-5 w-px bg-border sm:block" />

          {canRestore && template.isOverride && (
            <Button
              size="sm"
              variant="secondary"
              className="min-h-[44px] sm:min-h-0"
              onClick={() => void handleReset()}
              disabled={busy}
            >
              Reset to Platform
            </Button>
          )}

          {template.status === 'DISABLED'
            ? canEnable && (
                <Button
                  size="sm"
                  className="ml-auto min-h-[44px] sm:min-h-0"
                  onClick={() =>
                    void run(
                      () => emailTemplateLibraryService.enable(template.templateKey),
                      'Template enabled',
                    )
                  }
                  disabled={busy}
                >
                  Enable
                </Button>
              )
            : canDisable && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="ml-auto min-h-[44px] text-destructive hover:text-destructive sm:min-h-0"
                  onClick={() => void handleDisable()}
                  disabled={busy}
                >
                  Disable
                </Button>
              )}
        </footer>
      </aside>

      <SendTestDialog
        open={testOpen}
        templateKey={template.templateKey}
        templateName={template.name}
        gatewayName={gateways.find((g) => g.id === template.assignedGatewayId)?.name ?? null}
        onClose={() => setTestOpen(false)}
      />
    </>
  );
}
